"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader, PhoneCall, CircleCheck, CircleAlert } from "lucide-react";
const minimalistInputClass = "border-0 border-b-2 border-border rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary h-12 text-base md:text-lg transition-colors placeholder:text-muted-foreground/40";
const minimalistTextareaClass = "border-0 border-b-2 border-border rounded-none px-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:border-primary min-h-[100px] text-base md:text-lg transition-colors placeholder:text-muted-foreground/40 resize-y";
const minimalistLabelClass = "text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-1 block";
function ConsultationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
          <CircleCheck className="size-8" />
        </div>
        <div className="space-y-2">
          <h3 className="text-2xl font-default font-bold text-foreground">
            Mensaje Enviado
          </h3>
          <p className="text-muted-foreground max-w-md mx-auto">
            {successMessage}
          </p>
        </div>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary text-primary hover:bg-primary/5">
          Enviar otro mensaje
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="name" rules={{
        required: "El nombre es obligatorio"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={minimalistLabelClass}>Nombre Completo</FormLabel>
              <FormControl>
                <Input placeholder="Tu nombre aquí" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "El email es obligatorio",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Email no válido"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={minimalistLabelClass}>
                Correo Electrónico
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="hola@tuemail.com" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="phone" rules={{
        required: "El teléfono es obligatorio"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={minimalistLabelClass}>
                Teléfono de Contacto
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+34 600 000 000" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className={minimalistLabelClass}>
                Interés / Curso
              </FormLabel>
              <FormControl>
                <Input placeholder="Ej. Diseño Gráfico o Inglés" className={minimalistInputClass} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="preferred_date" render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={minimalistLabelClass}>
              Fecha para Entrevista Informativa
            </FormLabel>
            <FormControl>
              <Input type="date" className={minimalistInputClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="issue_description" rules={{
      required: "Por favor, cuéntanos tus dudas"
    }} render={({
      field
    }) => <FormItem className="space-y-1">
            <FormLabel className={minimalistLabelClass}>
              Tu Mensaje
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Cuéntanos sobre tus objetivos académicos y cómo podemos ayudarte..." className={minimalistTextareaClass} {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 rounded-md shadow-md transition-all">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Enviando...
          </> : "Enviar Mensaje"}
      </Button>
    </Form>;
}
export default function TechnicalConsultation() {
  return <section data-section-id="223786" id="hablemos" className="bg-background text-foreground py-16 md:py-24 lg:py-32 overflow-hidden border-t border-border/50">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24 items-start">
          {}
          <div className="lg:col-span-5 flex flex-col justify-center h-full space-y-8 lg:sticky lg:top-32">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-lg text-sm font-semibold tracking-widest uppercase">
                <PhoneCall className="size-4" />
                <span>TELÉFONO</span>
              </div>
              <h2 className="font-default font-bold text-5xl md:text-6xl lg:text-7xl tracking-tighter text-foreground leading-none">610 74&nbsp; 30&nbsp; 69<br /><br /></h2>
            </div>
            
            <div className="h-px w-24 bg-border"></div>
            
            <div className="space-y-6">
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-md font-default">
                Estamos aquí para guiarte en tu camino hacia el éxito profesional. Contáctanos hoy mismo.
              </p>
              <div className="grid grid-cols-2 gap-6 pt-4">
                <div>
                  <h4 className="font-semibold uppercase tracking-wider text-foreground mb-1 font-default !text-2xl">&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp;Email</h4>
                  <p className="text-muted-foreground font-default">centrocosmos@gmail.com</p>
                </div>
                <div>
                  <h4 className="font-semibold uppercase tracking-wider text-foreground font-default ![margin:80px_0px_4px_0px] !text-2xl">&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; Ubicación</h4>
                  <p className="text-muted-foreground font-default">Plaza de la Constitución.&nbsp; &nbsp; &nbsp; Palencia.</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 bg-card rounded-xl shadow-xl shadow-primary/5 border border-border/50 p-6 md:p-10 lg:p-12">
            <div className="mb-10">
              <h3 className="font-default text-3xl md:text-4xl font-bold text-foreground mb-3">
                Hablemos de tu futuro
              </h3>
              <p className="text-muted-foreground text-lg font-default">
                Completa el formulario y un asesor académico se pondrá en contacto contigo en menos de 24 horas.
              </p>
            </div>

            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="Contacto Academia" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            issue_description: "",
            preferred_date: ""
          }} submitText="Enviar Mensaje" successMessage="Gracias por tu interés. Hemos recibido tu mensaje y un asesor de CENTRO COSMOS te contactará muy pronto.">
              <ConsultationForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}